import { useMemo, useState } from "react";
import dayjs from "dayjs";
import { Trash2 } from "lucide-react";
import { ColumnDef } from "@tanstack/react-table";
import { createFileRoute } from "@tanstack/react-router";

import {
  DataTable,
  ColumnVisibilityDropdown,
  type Table
} from "@/components/datatable";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import {
  SearchFilterBar,
  useFilterBuilder,
  type FilterableAttribute
} from "@/components/search-filter-bar";
import { Button } from "@/components/ui/button";
import { SelectSearchable } from "@/components/forms";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { AddPartProcessSheet } from "./components/add-part-process-sheet";

import { useGetData } from "@/hooks/services/use-query-hooks";
import { useDebounce } from "@/hooks/use-debounce";

import { capitalize } from "@/lib/utils";
import { toastManager } from "@/lib/toast-manager";

import {
  REFERENCE_TABLE,
  useForeignKeyData
} from "@/hooks/use-foreign-key-data";
import {
  AddPartProcessData,
  DeletePartProcessData,
  GetPartProcesses,
  PartProcessFieldMetadata,
  UpdatePartProcessField
} from "@/hooks/services/pps/handlers/part-process-api";
import { MachineList } from "@/hooks/services/pps/handlers/machines-api";

import { getNestedValue, renderCell } from "../../list/utils";
import { JobOrderAttributeDataType } from "../../list/constant";

export const Route = createFileRoute(
  "/plant-3/(processes-screens)/processes-management/part-processes"
)({
  component: RouteComponent
});

function RouteComponent() {
  const [open, setOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPartProcess, setSelectedPartProcess] = useState<any>(null);
  const [tableInstance, setTableInstance] = useState<Table<any> | null>(null);
  const [hasTableData, setHasTableData] = useState(false);

  const [selectedPart, setSelectedPart] = useState<Record<string, any>>({});

  // Search and filter states
  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(searchText, 300);

  const { filters, ...filterActions } = useFilterBuilder();

  const processFieldMetadata = useGetData(PartProcessFieldMetadata({}));
  const [refetch, setRefetch] = useState<() => void>(() => {});

  // Use useForeignKeyData hooks for all foreign key types
  const { optionsData: partFilterOptions, setSearch: setPartFilterSearch } =
    useForeignKeyData(REFERENCE_TABLE.ppsPart, [
      "partNumber",
      "partDescription"
    ]);

  const { optionsData: partEditOptions, setSearch: setPartEditSearch } =
    useForeignKeyData(REFERENCE_TABLE.ppsPart, [
      "partNumber",
      "partDescription"
    ]);

  const { optionsData: processEditOptions, setSearch: setProcessEditSearch } =
    useForeignKeyData(REFERENCE_TABLE.ppsProcess, "processName");

  const { optionsData: shopEditOptions, setSearch: setShopEditSearch } =
    useForeignKeyData(REFERENCE_TABLE.ppsShop, "shopName");

  const handleRefetch = (callback: () => void) => {
    setRefetch(() => callback);
  };

  const filterableAttributes = useMemo<FilterableAttribute[]>(() => {
    return (
      processFieldMetadata?.data?.data?.map((item: any) => ({
        id: item.nestedColumnName || item.name,
        field: item.name,
        label: capitalize(item.label),
        dataType: item.dataType,
        nestedField: item.nestedColumnName,
        foreignKeyEndpoint: item.foreignKeyEntity || ""
      })) || []
    );
  }, [processFieldMetadata?.data?.data]);

  const columns = useMemo<ColumnDef<any, unknown>[]>(() => {
    return (
      processFieldMetadata?.data?.data?.map((item: any, index: number) => {
        const base: any = {
          header: capitalize(item.label),

          id: item.nestedColumnName
            ? `${item.name}-${item.nestedColumnName}`
            : `${item.name}-${index}`,
          cell: (cellInfo: any) => {
            switch (item.dataType) {
              case JobOrderAttributeDataType.DATETIME: {
                const value = getNestedValue(cellInfo.row.original, item);

                return value ? dayjs(value).format("DD-MMM-YYYY") : "";
              }

              default:
                return renderCell(cellInfo, item);
            }
          },
          type: item.dataType,
          attribute: item.name,
          nestedColumnName: item.nestedColumnName ?? "",
          referenceTable: item.foreignKeyEntity || "",
          editable: true
        };

        if (item.nestedColumnName) {
          base.accessorFn = (row: any) => getNestedValue(row, item);
        } else {
          base.accessorKey = item.name;
        }
        return base as ColumnDef<any, unknown>;
      }) || []
    );
  }, [processFieldMetadata?.data?.data]);

  const handleEditValue = (
    value: Record<string, string | number>,
    id: number,
    setEditingCell: (...args: any) => void
  ) => {
    const values = Object.entries(value);

    // eslint-disable-next-line prefer-const
    let body: { fieldName: string; fieldValue: string | number } = {
      fieldName: "",
      fieldValue: ""
    };

    if (values.length > 0) {
      let fieldName = values[0][0];
      const fieldValue = values[0][1];

      // Map foreign key entity names to their ID field names
      // When editing nested FK fields (ppsPart.*, ppsProcess.*, ppsShop.*, ppsMachine.*),
      // we need to send the FK ID field instead (partId, processId, shopId, machineId)
      const foreignKeyMapping: Record<string, string> = {
        ppsPart: "partId",
        ppsProcess: "processId",
        ppsShop: "shopId",
        ppsMachine: "machineId"
      };

      if (foreignKeyMapping[fieldName]) {
        fieldName = foreignKeyMapping[fieldName];
      }

      body.fieldName = fieldName;
      body.fieldValue = fieldValue.toString();
    }

    UpdatePartProcessField(id, body)
      .then(() => {
        setEditingCell(null);
        if (refetch) refetch();
        toastManager.showSuccess(
          "Field updated successfully",
          "The field has been updated."
        );
      })
      .catch((error: any) => {
        toastManager.showError(
          "Failed to update field",
          error?.message || "An unexpected error occurred."
        );
      });
  };

  const handleDeleteRow = (id: number) => {
    DeletePartProcessData(id)
      .then(() => {
        if (refetch) refetch();
        toastManager.showSuccess(
          "Part process deleted successfully",
          "The part process has been removed."
        );
      })
      .catch((error: any) => {
        toastManager.showError(
          "Failed to delete part process",
          error?.message || "An unexpected error occurred."
        );
      });
  };

  const handleConfirmDelete = () => {
    if (selectedPartProcess) {
      handleDeleteRow(selectedPartProcess.id);
      setDeleteDialogOpen(false);
      setSelectedPartProcess(null);
    }
  };

  const handleForeignKeySearch = (
    referenceTable: string,
    search: string,
    callback: (options: { label: string; value: string | number }[]) => void,
    row?: any
  ) => {
    if (referenceTable === REFERENCE_TABLE.ppsPart) {
      setPartEditSearch(search);
      callback(partEditOptions);
    } else if (referenceTable === REFERENCE_TABLE.ppsProcess) {
      setProcessEditSearch(search);
      callback(processEditOptions);
    } else if (referenceTable === REFERENCE_TABLE.ppsShop) {
      setShopEditSearch(search);
      callback(shopEditOptions);
    } else if (referenceTable === REFERENCE_TABLE.ppsMachine) {
      // Scope machine options to the shop of the row being edited.
      // The backend maps PpsProcess.PpsShop to a root-level ppsShop field — not nested under ppsProcess.
      const shopId: number | undefined =
        row?.original?.ppsShop?.id ?? undefined;
      MachineList({
        search: search || undefined,
        shopId,
        page: 1,
        pageSize: 100
      })
        .handle()
        .then((data: any) => {
          callback(
            (data?.data || []).map((m: any) => ({
              label: m.name || `ID: ${m.id}`,
              value: m.id
            }))
          );
        })
        .catch(() => callback([]));
    }
  };

  return (
    <>
      <SearchFilterBar
        placeholder="Search part or processes or machine name..."
        searchValue={searchText}
        onSearchChange={setSearchText}
        dynamicFilters={filters}
        filterableAttributes={filterableAttributes}
        {...filterActions}
        addOnFilter={[
          <SelectSearchable
            key="part-filter"
            value={selectedPart.value ?? null}
            onChange={(val) => {
              if (val != null) {
                const found = (partFilterOptions ?? []).find(
                  (o: { value: string | number; label: string }) =>
                    o.value.toString() === val.toString()
                );
                if (found) setSelectedPart(found);
              } else {
                setSelectedPart({});
              }
            }}
            onSearchChange={setPartFilterSearch}
            options={partFilterOptions || []}
            placeholder="Filter by part..."
            searchPlaceholder="Search part..."
            triggerClassName="aero:min-w-[200px]"
          />
        ]}
        showCreateButton
        createButtonLabel="Add Part Process"
        onCreateClick={() => setOpen(true)}
      />

      <Card className="aero:!rounded-lg aero:!border-neutral-200 aero:!p-0 aero:!gap-0 aero:!shadow-none aero:w-full aero:min-w-0 aero:!overflow-hidden">
        <CardHeader className="aero:!p-4">
          <div className="aero:flex aero:flex-col aero:lg:flex-row aero:items-start aero:justify-between aero:gap-4">
            <div>
              <h2 className="aero:text-base aero:md:text-lg aero:font-bold aero:leading-7 aero:tracking-normal aero:text-foreground">
                Part Process List
              </h2>
              <p className="aero:text-xs aero:md:text-sm aero:text-neutral-500 aero:leading-5 aero:tracking-normal">
                Configure part-specific process parameters and timing
              </p>
            </div>

            <div className="aero:flex aero:items-center aero:gap-2 aero:flex-shrink-0">
              {tableInstance && hasTableData && (
                <ColumnVisibilityDropdown table={tableInstance} />
              )}
            </div>
          </div>

          {/* Divider */}
          <div className="aero:w-full aero:h-0 aero:border-t aero:border-neutral-200 aero:my-4" />
        </CardHeader>

        <CardContent className="aero:!p-4 aero:min-w-0 aero:overflow-x-auto">
          <DataTable<any, any>
            api={(params) =>
              GetPartProcesses({
                ...params,
                partId: selectedPart.value || null,
                search: debouncedSearch || undefined
              })
            }
            columns={columns || []}
            filterParams={{
              partId: selectedPart.value || null,
              search: debouncedSearch || undefined
            }}
            onRefetch={handleRefetch}
            onEditValue={(value, id, callback) => {
              handleEditValue(value, id, callback);
            }}
            onForeignKeySearch={handleForeignKeySearch}
            onTableReady={(table) => {
              setTableInstance(table);
              setHasTableData(table.getRowModel().rows.length > 0);
            }}
            features={{
              enableSelection: false,
              enableEditing: true,
              enablePinning: true,
              enableResizing: true,
              enableColumnDragAndDrop: false,
              enableRowDragAndDrop: false,
              enableSorting: true,
              enablePagination: true
            }}
            actionItems={[
              {
                title: "Delete",
                icon: (
                  <Trash2 className="aero:h-4 aero:w-4 aero:text-destructive" />
                ),
                onClick: (row) => {
                  setSelectedPartProcess(row);
                  setDeleteDialogOpen(true);
                },
                className: "aero:bg-transparent aero:hover:bg-destructive/10"
              }
            ]}
            pageSizes={[10, 20, 50, 100]}
            defaultPageSize={10}
            emptyState={"No part processes found"}
          />
        </CardContent>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="aero:sm:max-w-[600px] aero:p-6 aero:gap-0">
            <DialogHeader className="aero:mb-4">
              <DialogTitle className="aero:font-inter aero:font-bold aero:text-xl aero:leading-9 aero:text-foreground">
                Delete Part Process
              </DialogTitle>
            </DialogHeader>

            <div className="aero:w-full aero:h-0 aero:border-t aero:border-neutral-400 aero:mb-4" />

            <p className="aero:text-sm aero:text-neutral-700">
              You are about to permanently delete the part process{" "}
              <strong>
                '{selectedPartProcess?.ppsPart?.partNumber} -{" "}
                {selectedPartProcess?.ppsProcess?.processName}'
              </strong>
              . This action cannot be undone.
            </p>

            <DialogFooter className="aero:mt-6 aero:pt-4 aero:border-t aero:border-neutral-200">
              <DialogClose asChild>
                <Button type="button" intent="neutral" design="outline">
                  Cancel
                </Button>
              </DialogClose>
              <Button
                type="button"
                intent="destructive"
                design="solid"
                className="aero:h-10 aero:gap-2 aero:px-4"
                onClick={handleConfirmDelete}
              >
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Part Process Dialog */}
        <AddPartProcessSheet
          open={open}
          onOpenChange={setOpen}
          onSubmit={(data, form) => {
            AddPartProcessData(data)
              .then(() => {
                if (refetch) refetch();
                form.reset();
                setOpen(false);

                toastManager.showSuccess(
                  "Part Process created successfully",
                  "The new part process has been added."
                );
              })
              .catch((error: any) => {
                toastManager.showError(
                  "Failed to create part process detail",
                  error?.message || "An unexpected error occurred."
                );
              });
          }}
        />
      </Card>
    </>
  );
}
